import React from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SparkleIcon, TrashIcon, EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";

const EMPTY = {
  version: "",
  title: "",
  body: "",
  cta_label: "",
  cta_url: "",
  image_url: "",
};

/**
 * Admin panel for managing "What's new in this build" entries.
 *
 * Endpoints used:
 *   GET    /api/admin/releases
 *   POST   /api/admin/releases
 *   PATCH  /api/admin/releases/{id}?active=...
 *   DELETE /api/admin/releases/{id}
 */
export default function AdminReleases() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [form, setForm] = React.useState(EMPTY);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/releases");
      setRows(data?.releases || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.version.trim() || !form.title.trim() || !form.body.trim()) {
      toast.error("Version, title and body are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/releases", {
        version: form.version.trim(),
        title: form.title.trim(),
        body: form.body.trim(),
        cta_label: form.cta_label.trim() || null,
        cta_url: form.cta_url.trim() || null,
        image_url: form.image_url.trim() || null,
      });
      toast.success(`Release v${form.version} published.`);
      setForm(EMPTY);
      load();
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (r) => {
    try {
      await api.patch(`/admin/releases/${r.id}?active=${!r.active}`);
      toast.success(r.active ? "Release deactivated." : "Release activated.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete release v${r.version}?`)) return;
    try {
      await api.delete(`/admin/releases/${r.id}`);
      toast.success("Release deleted.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div
      className="mt-10 border border-zinc-200 bg-white rounded-2xl overflow-hidden"
      data-testid="admin-releases-panel"
    >
      <div className="p-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display font-bold flex items-center gap-2">
            <SparkleIcon size={16} weight="fill" className="text-amber-500" />
            What's new — release notes
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            Each release pops a one-time modal for every user. They see it
            once per <span className="font-mono">version</span> string. Use
            this to highlight new features after each AAB build.
          </div>
        </div>
      </div>

      <form
        onSubmit={submit}
        className="p-4 grid sm:grid-cols-2 gap-3 bg-amber-50/40 border-b border-zinc-200"
        data-testid="admin-releases-form"
      >
        <div className="sm:col-span-1">
          <label className="text-xs font-semibold text-zinc-600">
            Version <span className="text-red-500">*</span>
          </label>
          <Input
            value={form.version}
            onChange={onChange("version")}
            placeholder="1.0.1"
            className="mt-1 font-mono"
            data-testid="admin-releases-version"
          />
        </div>
        <div className="sm:col-span-1">
          <label className="text-xs font-semibold text-zinc-600">
            Title <span className="text-red-500">*</span>
          </label>
          <Input
            value={form.title}
            onChange={onChange("title")}
            placeholder="Faster live streams + emoji tap reactions"
            className="mt-1"
            data-testid="admin-releases-title"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-zinc-600">
            Body (Markdown plain — newlines preserved){" "}
            <span className="text-red-500">*</span>
          </label>
          <Textarea
            value={form.body}
            onChange={onChange("body")}
            rows={4}
            placeholder={"• Streams now load 40% faster\n• Tap anywhere on the live video to drop reactions\n• Wholesale credentials review reduced to 24h"}
            className="mt-1"
            data-testid="admin-releases-body"
          />
        </div>
        <div className="sm:col-span-1">
          <label className="text-xs font-semibold text-zinc-600">
            CTA label (optional)
          </label>
          <Input
            value={form.cta_label}
            onChange={onChange("cta_label")}
            placeholder="See live now"
            className="mt-1"
            data-testid="admin-releases-cta-label"
          />
        </div>
        <div className="sm:col-span-1">
          <label className="text-xs font-semibold text-zinc-600">
            CTA URL (optional)
          </label>
          <Input
            value={form.cta_url}
            onChange={onChange("cta_url")}
            placeholder="/live"
            className="mt-1 font-mono"
            data-testid="admin-releases-cta-url"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-zinc-600">
            Image URL (optional)
          </label>
          <Input
            value={form.image_url}
            onChange={onChange("image_url")}
            placeholder="https://…/release-hero.png"
            className="mt-1 font-mono"
            data-testid="admin-releases-image-url"
          />
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <Button
            type="submit"
            disabled={saving}
            className="bg-[#1D78AB] hover:bg-[#165d85] text-white rounded-full px-5"
            data-testid="admin-releases-publish"
          >
            {saving ? "Publishing…" : "Publish release"}
          </Button>
        </div>
      </form>

      {loading ? (
        <div className="p-6 text-sm text-zinc-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-sm text-zinc-500" data-testid="admin-releases-empty">
          No releases yet. Publish your first to celebrate the next AAB build.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {rows.map((r) => (
            <li
              key={r.id}
              className="p-4 flex items-start gap-4 flex-wrap"
              data-testid={`admin-release-${r.id}`}
            >
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700">
                    v{r.version}
                  </span>
                  {r.active ? (
                    <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                      ACTIVE
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">
                      HIDDEN
                    </span>
                  )}
                  <span className="text-[11px] text-zinc-400 font-mono">
                    {r.created_at?.slice(0, 10)}
                  </span>
                </div>
                <div className="font-display font-bold mt-1">{r.title}</div>
                <div className="text-sm text-zinc-600 whitespace-pre-wrap mt-1 line-clamp-3">
                  {r.body}
                </div>
                {r.cta_url && (
                  <div className="text-xs text-[#1D78AB] mt-1 font-mono truncate">
                    CTA: {r.cta_label || "—"} → {r.cta_url}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => toggle(r)}
                  className="rounded-full gap-1.5"
                  data-testid={`admin-release-toggle-${r.id}`}
                >
                  {r.active ? <EyeSlashIcon size={14} /> : <EyeIcon size={14} />}
                  {r.active ? "Hide" : "Show"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => remove(r)}
                  className="rounded-full gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
                  data-testid={`admin-release-delete-${r.id}`}
                >
                  <TrashIcon size={14} /> Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
